import { useMutation } from '@tanstack/react-query'
import axiosClient from '../../axios'

export type ShareRole = 'viewer' | 'editor'

export interface ShareBlotCasePayload {
  caseId: string
  email: string
  role?: ShareRole
  /** Free-text lab role (e.g. "Technicien.ne de laboratoire") — only sent for role='editor'. */
  jobTitle?: string
  message?: string
}

export interface SharedBlotCaseResponse {
  success: boolean
  share: {
    id: string
    receiverEmail: string
    role: ShareRole
    jobTitle: string | null
    status: 'pending' | 'accepted' | 'revoked'
    sharedAt: string
  }
}

/**
 * Share a blot case with a colleague by email. The BE creates / refreshes
 * the LabBlotAnalysisShare row and emails the recipient a deep link to
 * the case carrying an opaque access token.
 *
 * Replaces the previous use of `useNewSharedDiagnostic`, which targeted
 * the legacy diagnostic-creation flow (`POST /diagnostics`) and never
 * actually shared the open blot case it was attached to.
 */
export function useShareBlotCase() {
  return useMutation({
    mutationKey: ['blot-analysis', 'share'],
    mutationFn: async ({ caseId, email, role = 'viewer', jobTitle, message }: ShareBlotCasePayload) => {
      const { data } = await axiosClient.post<SharedBlotCaseResponse>(
        `/blot-analysis/case/${caseId}/share`,
        { email, role, jobTitle, message },
      )
      return data
    },
  })
}
